import React, { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { uuidv7 as uuid } from "uuidv7";
import { isEqual, isEmpty } from "lodash";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { AccessLinkMetaField } from "src/app/metafields/AccessLinkMetaField/AccessLinkMetaField";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { Coupon } from "src/redux/coupon/couponReducer";
import { getCouponsList } from "src/redux/coupon/couponActions";
import { TooltipedEllipsis } from "src/app/components/TooltipedEllipsis/TooltipedEllipsis";
import { setGlobalTaskMetaValue } from "src/redux/creator-database/creatorDatabaseActions";
import { DictionaryElementSelector } from "src/app/dropdowns/DictionaryElementSelector/DictionaryElementSelector";
import { isAccessLevelReadOnly } from "src/app/components/GlideDataGrid/utils";
import RatingSelector from "src/app/components/RatingSelector/RatingSelector";
import { getRoute, ROUTES } from "src/utils/ROUTES";
import { RootState } from "src/redux/reducers";
import AggregatedSelect from "src/app/components/AggregatedSelect/AggregatedSelect";
import GlobalCreatorDropdown from "src/app/dropdowns/GlobalCreatorDropdown/GlobalCreatorDropdown";
import {
  changeTaskMetaFieldValue,
  getProjectDetails,
  setDictionaryElementMetaValue,
  setTableInputFocused,
  updateProjectMetaFieldOnList,
  updateProjectMetaValue,
} from "src/redux";
import Select from "../../../components/Select/Select";
import ChoosePersonDropdown from "../../../dropdowns/ChoosePersonDropdown/ChoosePersonDropdown";
import CustomDatePicker, {
  DatePickerWrapper,
} from "../../../components/CustomDatePicker/CustomDatePicker";
import { showToast } from "../../../methods/showToast";
import { Metadata } from "../../Subtask/Subtask";
import { ACTION_TYPES } from "../../../project/TableField/utils";

import AvatarPlaceholder from "../../../../images/avatar-purple.svg";
import ContentPlaceholder from "../../../../images/content-placeholder.svg";
import PublicationPlaceholder from "../../../../images/publication-placeholder-grey-sm.svg";
import { ReactComponent as ShareAltIcon } from "../../../../images/share-alt.svg";
import { ReactComponent as RemoveIcon } from "../../../../images/close-alt.svg";
import { ReactComponent as EditIcon } from "../../../../images/edit.svg";
import { ReactComponent as PlusIcon } from "../../../../images/plus-transparent.svg";

import { IMember } from "../../../project/ProjectTypes";
import AddButton from "../../../components/AddButton/AddButton";
import { SeperatedNumberInput } from "../../../components/MaskedInputs/SeperatedNumberInput";
import { PercentInput } from "../../../components/MaskedInputs/PercentInput";
import { CurrencyInput } from "../../../components/MaskedInputs/CurrencyInput";
import { tableDataType } from "../../../components/Table/Table";
import { FieldSynchronizationWarningModal } from "../../../modals/FieldSynchronizationWarningModal/FieldSynchronizationWarningModal";
import { MultipleFileField } from "../../../metafields/MultipleFileField/MultipleFileField";
import { TextBoxWrapper } from "../../../components/TextBoxWrapper/TextBoxWrapper";
import { getImageSource, splitNumbers } from "../../../../utils/methods";
import SelectDropdown from "../../../dropdowns/SelectDropdown/SelectDropdown";
import { isMetaFieldRemovable, showValueIsRequiredError } from "./utils";
import ProjectSelector from "../../../components/ProjectSelector/ProjectSelector";
import { REGEX } from "src/utils/REGEX";
import { ContentMonitorScore } from "src/app/project/TableField/meta-types/ContentMonitorScore/ContentMonitorScore";
import ChooseActionDropdownContainer from "./components/ChooseActionDropdownContainer";
import useGetProjectTaskAutocompleteList from "src/app/Task/hooks/useGetProjectTaskAutocompleteList";
import { useCellCurrencyState } from "src/hooks/useCellCurrencyState";

interface SingleMetaFieldProps {
  metaData: Metadata;
  dataType: tableDataType;
  externalTaskId?: string;
  externalProjectId?: string;
  wsGlobalProjectMetaFieldUuid?: string;
  columnValueSource?: string | null;
  overrideValue?: boolean;
  blockManualEdit?: boolean;
  handleUpdateMetaValue?: (newValue: any, uuid: string) => void;
}

function SingleMetaField({
  metaData,
  dataType,
  externalTaskId,
  externalProjectId,
  wsGlobalProjectMetaFieldUuid,
  columnValueSource,
  overrideValue,
  blockManualEdit,
  handleUpdateMetaValue,
}: SingleMetaFieldProps) {
  const {
    value,
    setValue,
    cellCurrencyCode,
    hasCustomCurrency,
    handleCurrencyPickerSave,
  } = useCellCurrencyState(metaData.value, metaData?.data);
  const [placeholderValue, setPlaceholderValue] = useState("");
  const [member, setMember] = useState<any>({});
  const [action, setAction] = useState<any>({});

  const [showEditMark, setShowEditMark] = useState(metaData.manuallyEdited);
  const [disableFieldClick, setDisableFieldClick] = useState(
    metaData.shouldTriggerEditWarning,
  );
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isEditingCurrency, setIsEditingCurrency] = useState(false);
  const isCurrencyPickerOpenRef = useRef(false);

  const handleImageLoadError = (
    placeholder: string,
    e: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = placeholder;
  };

  const params = useParams<{
    projectId: string;
    taskId: string;
    dictionaryUuid: string;
  }>();
  const history = useHistory();
  const dispatch = useDispatch();

  const fieldContentRef = useRef<HTMLSpanElement>(null);
  const placeholderRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<any>();

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const couponsList = useSelector(
    (state: RootState) => state.couponReducer.couponsList,
  );

  const membersList = useSelector(
    (state: RootState) => state.projectReducer.activeMembersList,
  );
  const projectBasicData = useSelector(
    (state: RootState) => state.projectReducer.projectBasicData,
  );

  const projectId =
    params.projectId || projectBasicData?.projectId || externalProjectId;

  const projectTasksAutocomplete = useGetProjectTaskAutocompleteList(
    projectId ?? null,
  );

  const fieldType = metaData.type;

  const updateMetaValue = async (
    newValue: any,
    customCurrencyCode?: string | null,
  ) => {
    try {
      switch (dataType) {
        case tableDataType.Dictionary:
          if (externalTaskId) {
            dispatch(
              setDictionaryElementMetaValue(
                [externalTaskId],
                metaData.uuid,
                newValue,
              ),
            );
          }
          break;

        case tableDataType.GlobalTask:
          if (externalTaskId) {
            dispatch(
              setGlobalTaskMetaValue(
                externalTaskId,
                metaData.uuid,
                newValue,
                customCurrencyCode,
              ),
            );
          }
          break;

        case tableDataType.Task:
          dispatch(
            changeTaskMetaFieldValue(
              params.taskId,
              metaData.uuid,
              newValue,
              customCurrencyCode,
            ),
          );
          break;

        case tableDataType.ProjectDetails:
          await dispatch(
            updateProjectMetaValue(
              params.projectId,
              metaData.uuid,
              newValue,
              customCurrencyCode,
            ),
          );
          dispatch(getProjectDetails(params.projectId));
          break;

        case tableDataType.Project:
          if (externalProjectId && wsGlobalProjectMetaFieldUuid) {
            dispatch(
              updateProjectMetaFieldOnList(
                externalProjectId,
                metaData.uuid,
                newValue,
                wsGlobalProjectMetaFieldUuid,
                customCurrencyCode,
              ),
            );
          }
          break;
        default:
          break;
      }

      if (handleUpdateMetaValue) {
        handleUpdateMetaValue(newValue, metaData.uuid);
      }

      if (metaData.shouldTriggerEditWarning) {
        setShowEditMark(true);
      }
    } catch (error) {
      showToast(
        "error",
        <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
        <IDHFormattedMessage
          id="ws_could_not_update_meta_field"
          defaultMessage="Could not update meta field."
        />,
      );
    }
  };

  const objectId = useMemo(() => {
    if (dataType === tableDataType.Task) {
      return params.taskId;
    }
    if (externalTaskId) {
      return externalTaskId;
    }
    if (externalProjectId) {
      return externalProjectId;
    }

    return params.projectId;
  }, [dataType, params, externalTaskId, externalProjectId]);

  useEffect(() => {
    if (isEditingCurrency) {
      inputRef.current?.focus();
    }
  }, [isEditingCurrency]);

  useEffect(() => {
    if (member?.id && metaData.value !== member?.id) {
      updateMetaValue(member.id);
    }
  }, [member]);

  useEffect(() => {
    if (action?.taskId && metaData.value !== action?.taskId) {
      updateMetaValue(action.taskId);
    }
  }, [action]);

  useEffect(() => {
    if (metaData.type === MetaFieldType.Member && membersList?.length) {
      const memberCopy = membersList.find(
        (member: IMember) => member.id === metaData.value,
      );

      setMember(memberCopy);
    }
  }, [metaData.value, membersList]);

  useEffect(() => {
    setDisableFieldClick(metaData.shouldTriggerEditWarning);
  }, [metaData.shouldTriggerEditWarning]);

  useEffect(() => {
    setShowEditMark(metaData.manuallyEdited);
  }, [metaData.manuallyEdited]);

  useEffect(() => {
    if (ACTION_TYPES.includes(fieldType) && projectTasksAutocomplete) {
      const actionCopy = projectTasksAutocomplete.find(
        (item) => item.taskId === metaData.value,
      );

      setAction({
        ...actionCopy,
        metaFieldId: metaData.uuid,
        metaFieldValue: metaData.value,
        metaFieldName: metaData.name,
      });
    }
  }, [
    metaData.uuid,
    metaData.value,
    metaData.name,
    fieldType,
    projectTasksAutocomplete,
    setAction,
  ]);

  const onDateChange = async (newDate: any) => {
    if (metaData.uuid || externalProjectId) {
      try {
        await updateMetaValue(newDate);

        setValue((prevValue: any) => ({
          ...prevValue,
          date: newDate,
        }));
      } catch (error) {
        showToast(
          "error",
          <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
          <IDHFormattedMessage
            id="ws_could_not_change_due_date"
            defaultMessage="Could not change due date."
          />,
        );
      }
    }
  };

  const removeValue = () => {
    updateMetaValue(null);
    setValue(null);
  };

  const handleUpdateValue = (
    inputValue: string | null,
    isRemovable: boolean,
    customCurrencyCode?: string | null,
  ) => {
    if (
      dataType === tableDataType.GlobalMetaFieldProjectWizardFields ||
      dataType === tableDataType.DictionaryRequiredFields
    ) {
      if (isEmpty(inputValue)) {
        removeValue();
      } else {
        setValue(inputValue);
        updateMetaValue(inputValue, customCurrencyCode);
      }
      return;
    }

    if (isEmpty(inputValue) && !isEmpty(metaData.value) && !isRemovable) {
      setValue(metaData.value);
      showValueIsRequiredError();
      return;
    }

    if (isEmpty(inputValue) && !isEmpty(metaData.value) && isRemovable) {
      removeValue();
      return;
    }

    if (isEmpty(inputValue) && isEmpty(metaData.value)) {
      setValue(null);
      return;
    }

    // changing only the currency keeps the typed number, so compare it separately
    const persistedCurrencyCode =
      metaData?.data?.customCurrencyCode || metaData?.data?.currencyCode;
    const hasCurrencyChanged =
      Boolean(customCurrencyCode) &&
      customCurrencyCode !== persistedCurrencyCode;

    if (!isEqual(inputValue, metaData.value) || hasCurrencyChanged) {
      updateMetaValue(inputValue, customCurrencyCode);
    }
  };

  const inputOnBlur = (isRemovable: boolean, isReadOnly: boolean) => {
    if (isReadOnly) return;
    if (isCurrencyPickerOpenRef.current) return;
    handleUpdateValue(
      value,
      isRemovable,
      hasCustomCurrency ? cellCurrencyCode : null,
    );
  };

  const inputOnBlurWithValue = (newValue: string, isRemovable: boolean) => {
    handleUpdateValue(newValue, isRemovable);
  };

  const [tempWidth, setTempWidth] = useState(50);

  const handleTaskOpen = (targetTaskId: string) => {
    history.push(
      getRoute(ROUTES.TASK_DETAILS, {
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsProjectUuid: projectId,
        wsTaskUuid: targetTaskId,
      }),
    );
  };

  function SingleMetaFieldAddButton() {
    if (columnValueSource) {
      // disable create local values if parent global meta field has value source
      return null;
    }

    return (
      <AddButton
        variant="rect"
        onClick={() => {
          setValue("");
          if (metaData.type === MetaFieldType.Currency) {
            setIsEditingCurrency(true);
          }
          setTimeout(() => {
            inputRef.current?.focus();
          }, 15);
        }}
      />
    );
  }

  const clearMetaField = () => {
    updateMetaValue(null);
    setValue(null);
  };

  useEffect(() => {
    if (placeholderRef?.current) {
      setTempWidth(placeholderRef?.current?.clientWidth);
    }
  }, [placeholderValue]);

  const isProjectCurrencyInitialized = !(
    dataType === tableDataType.Project &&
    externalProjectId &&
    metaData.value === null
  );

  const isValueSourceReadOnly = Boolean(
    (dataType === tableDataType.Project ||
      dataType === tableDataType.GlobalTask ||
      dataType === tableDataType.ProjectDetails) &&
      metaData?.valueSource &&
      metaData.valueSource.length > 0,
  );

  const isMetaFieldSharedAndNotEditable =
    metaData?.shared && !metaData?.sharedEditable;

  const isClientOrSeeAsClient =
    projectBasicData?.seeAsClientMode ||
    projectBasicData?.isCurrentMemberClient;

  const isEditRestrictedByFieldAccess = useSelector(
    (state: RootState) =>
      state.taskReducer.tasksColumns.find(
        (column) => column.metaFieldId === metaData.uuid,
      )?.metaFieldCanEdit === false,
  );

  const isReadOnly = Boolean(
    overrideValue ||
      blockManualEdit ||
      isEditRestrictedByFieldAccess ||
      isAccessLevelReadOnly(metaData.accessLevel) ||
      isValueSourceReadOnly ||
      (isClientOrSeeAsClient && isMetaFieldSharedAndNotEditable),
  );

  const isRemovable = isMetaFieldRemovable(dataType, metaData.valueRequired);
  const isContentMonitor = Boolean(metaData.data?.wsCreatorContentMonitorUuid);

  useEffect(() => {
    if (inputRef?.current?.value) {
      setPlaceholderValue(inputRef.current.value);
    }
  }, [value, metaData.value, metaData.type, metaData.data]);

  const renderMetaField = () => {
    const wsSelectDataSetUuid = metaData?.data?.wsSelectDataSetUuid;

    if (isContentMonitor && projectId) {
      const match = metaData.fieldValueSource?.match(REGEX.UUID);
      const monitorUuid =
        match?.[0] ?? metaData.data.wsCreatorContentMonitorUuid!;

      return (
        <ContentMonitorScore
          creatorId={params.taskId}
          monitorId={monitorUuid}
          value={value}
          projectId={projectId}
          status={metaData.data.ccmStatus}
          tooltip={metaData.data.tooltip}
        />
      );
    }

    switch (metaData.type) {
      case MetaFieldType.Text:
        return value || value === "" ? (
          <div className="value-field">
            <TextBoxWrapper
              ref={inputRef}
              className="value-textbox"
              value={value || ""}
              onFocus={() => dispatch(setTableInputFocused(true))}
              onChange={(v: any) => {
                if (value !== v) setValue(v);
              }}
              onBlur={(newValue: string) => {
                inputOnBlurWithValue(newValue, isRemovable);
                dispatch(setTableInputFocused(false));
              }}
              readOnly={isReadOnly}
            />
            {value && !isReadOnly && isRemovable && (
              <RemoveIcon className="value-field__icon" onClick={removeValue} />
            )}
          </div>
        ) : (
          !isReadOnly && <SingleMetaFieldAddButton />
        );

      case MetaFieldType.Percent: {
        return value || value === "" ? (
          <div className="value-field">
            <span ref={placeholderRef} className="value-placeholder">
              {placeholderValue}
            </span>
            <PercentInput
              inputRef={inputRef}
              className="value-input"
              rawValue={value}
              setRawValue={setValue}
              onBlur={() => inputOnBlur(isRemovable, isReadOnly)}
              style={{ width: `${tempWidth}px` }}
              readOnly={isReadOnly}
            />
            {showEditMark && metaData.value && !isReadOnly && (
              <div className="task-meta-fields__value-content-edit-icon">
                <EditIcon />
                <IDHFormattedMessage id="ws_edited" defaultMessage="Edited" />
              </div>
            )}

            {!disableFieldClick && value && !isReadOnly && isRemovable && (
              <RemoveIcon className="value-field__icon" onClick={removeValue} />
            )}
          </div>
        ) : (
          !isReadOnly && <SingleMetaFieldAddButton />
        );
      }

      case MetaFieldType.Currency: {
        const currencyCode = metaData?.data?.currencyCode;
        const showCurrencyPrefix =
          isProjectCurrencyInitialized &&
          dataType !== tableDataType.GlobalMetaFieldProjectWizardFields;
        // Only the project details sidebar (a single field per row, same density
        // as the tasks table) gets the click-to-edit preview. Every other
        // consumer of this field (projects list, global task panel, wizard)
        // always showed a live input for every field type before currencies
        // existed here, and must keep doing so - the projects list in
        // particular reads this value straight out of an <input>.
        const usesClickToEditPreview =
          dataType === tableDataType.ProjectDetails;

        return value || value === "" ? (
          <div className="value-field value-field--currency">
            {(!usesClickToEditPreview || isEditingCurrency) && !isReadOnly ? (
              <CurrencyInput
                inputRef={inputRef}
                className="value-input value-input--currency"
                rawValue={value}
                setRawValue={setValue}
                onFocus={() => setIsEditingCurrency(true)}
                onBlur={() => {
                  inputOnBlur(isRemovable, isReadOnly);
                  if (!isCurrencyPickerOpenRef.current) {
                    setIsEditingCurrency(false);
                  }
                }}
                prefix={showCurrencyPrefix ? cellCurrencyCode : ""}
                columnCurrencyCode={currencyCode || ""}
                onCurrencyChange={
                  showCurrencyPrefix ? handleCurrencyPickerSave : undefined
                }
                onCurrencyPickerOpenChange={
                  showCurrencyPrefix
                    ? (isOpen: boolean) => {
                        isCurrencyPickerOpenRef.current = isOpen;
                      }
                    : undefined
                }
                readOnly={isReadOnly}
              />
            ) : (
              <div
                className="value-input value-input--currency value-input--currency-preview"
                onClick={() => !isReadOnly && setIsEditingCurrency(true)}
              >
                {showCurrencyPrefix && (
                  <span className="currency-code">{cellCurrencyCode}</span>
                )}
                <span className="currency-value">{splitNumbers(value)}</span>
              </div>
            )}

            {showEditMark && metaData.value && !isReadOnly && (
              <div className="task-meta-fields__value-content-edit-icon">
                <EditIcon />
                <IDHFormattedMessage id="ws_edited" defaultMessage="Edited" />
              </div>
            )}

            {!disableFieldClick &&
              !isEditingCurrency &&
              value &&
              !isReadOnly &&
              isRemovable && (
                <RemoveIcon
                  className="value-field__icon"
                  onClick={removeValue}
                />
              )}
          </div>
        ) : (
          !isReadOnly && <SingleMetaFieldAddButton />
        );
      }

      case MetaFieldType.Number:
      case MetaFieldType.SocialUrl: {
        return value || value === "" ? (
          <div className="value-field">
            <span ref={placeholderRef} className="value-placeholder">
              {placeholderValue}
            </span>
            <SeperatedNumberInput
              inputRef={inputRef}
              className="value-input"
              rawValue={value}
              setRawValue={(newValue: string) => setValue(newValue)}
              onBlur={() => inputOnBlur(isRemovable, isReadOnly)}
              style={{ width: `${tempWidth}px` }}
              readOnly={isReadOnly}
            />

            {showEditMark && metaData.value && !isReadOnly && (
              <div className="task-meta-fields__value-content-edit-icon">
                <EditIcon />
                <IDHFormattedMessage id="ws_edited" defaultMessage="Edited" />
              </div>
            )}
            {!disableFieldClick && value && !isReadOnly && isRemovable && (
              <RemoveIcon className="value-field__icon" onClick={removeValue} />
            )}
          </div>
        ) : (
          !isReadOnly && <SingleMetaFieldAddButton />
        );
      }

      case MetaFieldType.Member:
        return (
          <div className="value-field">
            {member?.id && (
              <>
                <ChoosePersonDropdown
                  value={member}
                  choosePerson={setMember}
                  uuid={metaData?.uuid}
                  dataType={dataType}
                  wsTeamUuids={metaData?.data?.wsTeamUuids}
                  globalFieldId={
                    metaData?.wsGlobalTaskMetaFieldUuid ||
                    metaData?.wsGlobalProjectMetaFieldUuid
                  }
                  disabled={isReadOnly}
                >
                  <div
                    className={classNames(
                      "assignee-field",
                      isReadOnly && "assignee-field--disabled",
                    )}
                  >
                    <img
                      src={member.avatarUrl || AvatarPlaceholder}
                      alt="avatar"
                    />
                    <TooltipedEllipsis
                      className="assignee-field__username"
                      maxWidth={
                        dataType === tableDataType.Project && externalProjectId
                          ? 125
                          : 250
                      }
                      content={member.name}
                    />
                  </div>
                </ChoosePersonDropdown>
                {!isReadOnly && isRemovable && (
                  <RemoveIcon
                    className="value-field__icon"
                    onClick={() => {
                      removeValue();
                      setMember(null);
                    }}
                  />
                )}
              </>
            )}
            {!member?.id && !isReadOnly && (
              <ChoosePersonDropdown
                value={member}
                choosePerson={setMember}
                uuid={metaData?.uuid}
                dataType={dataType}
                wsTeamUuids={metaData?.data?.wsTeamUuids}
                globalFieldId={
                  metaData?.wsGlobalTaskMetaFieldUuid ||
                  metaData?.wsGlobalProjectMetaFieldUuid
                }
              >
                {!isReadOnly && <AddButton variant="circle" />}
              </ChoosePersonDropdown>
            )}
          </div>
        );

      case MetaFieldType.SingleSelect:
        return (
          <div className="value-field">
            <SelectDropdown
              uuid={metaData?.uuid}
              dataType={dataType}
              fieldData={metaData}
              fieldValue={value}
              onValueChange={updateMetaValue}
              selectOptionType="single"
              isRemovable={isRemovable}
              wsSelectDataSetUuid={wsSelectDataSetUuid}
              emptyFieldPlaceholder={<AddButton variant="rect" />}
              readOnly={isReadOnly}
              removeValueButton={
                <RemoveIcon
                  className="value-field__icon"
                  onClick={clearMetaField}
                />
              }
              qaContext={dataType}
            />
          </div>
        );

      case MetaFieldType.MultiSelect:
        return (
          <div className="value-field">
            <SelectDropdown
              uuid={metaData?.uuid}
              dataType={dataType}
              fieldData={metaData}
              fieldValue={value}
              onValueChange={updateMetaValue}
              selectOptionType="multi"
              isRemovable={isRemovable}
              wsSelectDataSetUuid={wsSelectDataSetUuid}
              stackLabels={
                dataType === tableDataType.GlobalMetaFieldProjectWizardFields
              }
              emptyFieldPlaceholder={<AddButton variant="rect" />}
              readOnly={isReadOnly}
              removeValueButton={
                value && (
                  <RemoveIcon
                    className="value-field__icon"
                    onClick={clearMetaField}
                  />
                )
              }
            />
          </div>
        );

      case MetaFieldType.GlobalCreator:
        return (
          <div className="value-field">
            <GlobalCreatorDropdown
              fieldValue={value}
              handleUpdateValue={(values: string[]) => {
                updateMetaValue(values);
              }}
              readOnly={isReadOnly}
            />
            {value && !isReadOnly && isRemovable && (
              <RemoveIcon
                className="value-field__icon"
                onClick={clearMetaField}
              />
            )}
          </div>
        );

      case MetaFieldType.AggregatedSelect:
        return (
          <div className="value-field">
            <AggregatedSelect
              uuid={metaData?.uuid}
              optionsData={metaData?.data?.singleSelectOptions}
              fieldValue={value}
              updateValue={updateMetaValue}
              emptyFieldPlaceholder={<AddButton variant="rect" />}
              readOnly={isReadOnly}
            />
            {value && !isReadOnly && isRemovable && (
              <RemoveIcon
                className="value-field__icon"
                onClick={clearMetaField}
              />
            )}
          </div>
        );

      case MetaFieldType.Coupon: {
        const couponOptions = couponsList
          .filter((coupon: Coupon) =>
            coupon.isAssigned ? coupon?.wsTask?.taskId === objectId : true,
          )
          .map((coupon: Coupon) => ({
            name: coupon.code,
            value: coupon.uuid,
            rank: "a",
            color: "45485b",
            backgroundColor: "f6f6f7",
          }));

        return (
          <>
            <Select
              mode="single"
              optionsData={couponOptions}
              uuid={metaData?.uuid || ""}
              fieldValue={value}
              fieldName={metaData?.name}
              variant="disable_all"
              updateValue={(newValue: any) => {
                setValue(newValue);
                updateMetaValue(newValue);
                dispatch(getCouponsList(activeWorkspaceUuid));
              }}
              emptyFieldPlaceholder={
                !isReadOnly && (
                  <div className="tasks-table__empty-field">
                    <PlusIcon />
                  </div>
                )
              }
              dataType={dataType}
              readOnly={isReadOnly}
            />

            {value && !isReadOnly && isRemovable && (
              <RemoveIcon className="value-field__icon" onClick={removeValue} />
            )}
          </>
        );
      }

      case MetaFieldType.Date:
        return (
          <div className="value-field">
            <DatePickerWrapper value={value?.date} readOnly={isReadOnly}>
              <CustomDatePicker
                initialDate={value?.date}
                dateChangeHandler={onDateChange}
                readOnly={isReadOnly}
              />
            </DatePickerWrapper>

            {value && !isReadOnly && isRemovable && (
              <RemoveIcon className="value-field__icon" onClick={removeValue} />
            )}
          </div>
        );

      case MetaFieldType.Creator:
      case MetaFieldType.Content:
      case MetaFieldType.PublicationRelated: {
        return (
          <div className="value-field">
            <ChooseActionDropdownContainer
              projectId={projectId}
              fieldType={fieldType}
              isReadOnly={isReadOnly}
              action={action}
              setAction={setAction}
            >
              <span
                className={classNames("action-field", {
                  "action-field--circle":
                    metaData.type === MetaFieldType.Creator,
                })}
              >
                {action?.title && (
                  <>
                    {metaData.type === MetaFieldType.Creator ? (
                      <img
                        src={getImageSource(
                          action?.cover,
                          "tiny",
                          AvatarPlaceholder,
                        )}
                        alt="avatar"
                        onError={(e) =>
                          handleImageLoadError(AvatarPlaceholder, e)
                        }
                      />
                    ) : metaData.type === MetaFieldType.PublicationRelated ? (
                      <img
                        src={getImageSource(
                          action?.cover,
                          "tiny",
                          PublicationPlaceholder,
                        )}
                        alt="publication"
                        onError={(e) =>
                          handleImageLoadError(PublicationPlaceholder, e)
                        }
                      />
                    ) : (
                      <img
                        src={getImageSource(
                          action?.cover,
                          "tiny",
                          ContentPlaceholder,
                        )}
                        alt="content"
                        onError={(e) =>
                          handleImageLoadError(ContentPlaceholder, e)
                        }
                      />
                    )}
                    {action?.title}
                  </>
                )}
                {!action?.title && !isReadOnly && (
                  <AddButton
                    variant={
                      metaData.type === MetaFieldType.Creator
                        ? "circle"
                        : "square"
                    }
                  />
                )}
              </span>
            </ChooseActionDropdownContainer>

            {action?.title && (
              <>
                <ShareAltIcon
                  className="value-field__icon"
                  onClick={() => {
                    handleTaskOpen(action.taskId);
                  }}
                />

                {!isReadOnly && isRemovable && (
                  <RemoveIcon
                    className="value-field__icon"
                    onClick={() => {
                      setAction(null);
                      removeValue();
                    }}
                  />
                )}
              </>
            )}
          </div>
        );
      }
      case MetaFieldType.AccessLink:
        return (
          <AccessLinkMetaField
            accessLinkValue={value}
            uuid={metaData?.uuid}
            objectId={objectId}
            dataType={dataType}
            readOnly={isReadOnly}
          />
        );
      case MetaFieldType.File:
        return (
          <MultipleFileField
            files={value?.length ? value : []}
            uuid={metaData.uuid || uuid()}
            objectId={objectId}
            dataType={dataType}
            readOnly={isReadOnly}
          />
        );
      case MetaFieldType.BoolVal:
        return (
          <CustomCheckbox
            id={
              metaData.uuid && objectId
                ? `${metaData.uuid}-${objectId}2`
                : uuid()
            }
            name={metaData.uuid}
            checked={value}
            onChange={(e) => {
              setValue(e.target.checked);
              updateMetaValue(e.target.checked);
            }}
            disabled={isReadOnly}
            blue
          />
        );

      case MetaFieldType.DictionaryElement: {
        const wsDictionaryUuid = metaData?.data?.wsDictionaryUuid;

        const wsDictionarySubFieldUuid =
          metaData?.data?.wsDictionarySubFieldUuid;

        return (
          <div className="value-field">
            <DictionaryElementSelector
              onValueChange={updateMetaValue}
              value={metaData.value}
              wsDictionaryUuid={wsDictionaryUuid}
              wsDictionarySubFieldUuid={wsDictionarySubFieldUuid}
              wsDictionaryElementsFilter={
                metaData?.data?.wsDictionaryElementsFilter
              }
              disabled={isReadOnly}
              dataType={dataType}
              rowId={
                dataType === tableDataType.Dictionary
                  ? externalTaskId
                  : params.taskId
              }
            />

            {value && !isReadOnly && isRemovable && (
              <RemoveIcon className="value-field__icon" onClick={removeValue} />
            )}
          </div>
        );
      }

      case MetaFieldType.Rating:
        return (
          <RatingSelector
            value={parseInt(value || "0", 10)}
            disabled={isReadOnly}
            onValueChange={(i) => {
              updateMetaValue(i);
              setValue(i);
            }}
          />
        );

      case MetaFieldType.Project:
        return (
          <ProjectSelector
            value={metaData.value}
            wsWorkspaceUuid={activeWorkspaceUuid}
          />
        );
    }
  };

  const onFieldClick = () => {
    if (disableFieldClick && !showWarningModal) {
      setShowWarningModal(true);
    }
  };

  const confirmWarning = () => {
    setDisableFieldClick(false);
    setShowWarningModal(false);

    fieldContentRef?.current?.click();
  };

  const nonReadOnly = isContentMonitor;

  return (
    <div className="task-meta-fields__value" onClick={onFieldClick}>
      <span
        ref={fieldContentRef}
        className={classNames("task-meta-fields__value-content", {
          "task-meta-fields__value-content--disabled": disableFieldClick,
          "task-meta-fields__value-content--read-only":
            isReadOnly && !nonReadOnly,
        })}
      >
        {renderMetaField()}
      </span>

      {showWarningModal && (
        <FieldSynchronizationWarningModal
          onClose={() => setShowWarningModal(false)}
          onAccept={confirmWarning}
        />
      )}
    </div>
  );
}

export default SingleMetaField;
